import React, { useState } from 'react';
import { FaBookmark, FaChartBar, FaChartLine, FaSearch, FaBell, FaUserCircle, FaBars, FaTimes, FaTwitter, FaLinkedin, FaGithub, FaEnvelope, FaRegPaperPlane, FaTimesCircle } from 'react-icons/fa';
import "./Dashboard.css";

const MOCK_STOCKS = {
  RELIANCE: { price: 2850.25, change: "+12.30", percentChange: "+0.43%" },
  TCS: { price: 3725.10, change: "-8.50", percentChange: "-0.23%" },
  HDFC: { price: 1680.00, change: "+5.00", percentChange: "+0.30%" },
  INFY: { price: 1452.75, change: "+2.10", percentChange: "+0.14%" },
  ICICIBANK: { price: 950.60, change: "-3.20", percentChange: "-0.34%" },
};

const DEFAULT_SYMBOLS = ["RELIANCE", "TCS", "HDFC", "INFY", "ICICIBANK"];

const MOCK_AI_RESPONSES = {
  RELIANCE: [
    "Reliance Industries is a diversified conglomerate with strong presence in energy, retail, and telecom.",
    "The stock has shown steady growth over the past year, supported by robust earnings and expansion in Jio and retail segments.",
    "AI Insight: Based on recent trends, Reliance is well-positioned for long-term growth, but short-term volatility may occur due to global crude prices."
  ],
  TCS: [
    "TCS is a global IT services leader with consistent revenue growth and high profit margins.",
    "The company benefits from digital transformation trends and a strong client base.",
    "AI Insight: TCS remains a defensive stock with stable returns, ideal for long-term portfolios."
  ],
  HDFC: [
    "HDFC is a leading financial institution with a strong loan portfolio and low NPAs.",
    "The stock is favored for its stability and consistent dividend payouts.",
    "AI Insight: HDFC is a solid choice for conservative investors seeking steady growth."
  ],
  INFY: [
    "Infosys is a top IT exporter, known for innovation and digital services.",
    "The company has a healthy order book and strong management guidance.",
    "AI Insight: Infosys is expected to benefit from global IT spending, but watch for currency fluctuations."
  ],
  ICICIBANK: [
    "ICICI Bank is one of India's largest private sector banks, showing strong loan growth and improving asset quality.",
    "Recent quarters have seen robust profit growth and digital adoption.",
    "AI Insight: ICICI Bank is poised for further upside as credit demand rises in India."
  ]
};

function Dashboard() {
  const [navOpen, setNavOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [symbols, setSymbols] = useState(DEFAULT_SYMBOLS);
  const [selectedStock, setSelectedStock] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatStock, setChatStock] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");

  function handleNavToggle() {
    setNavOpen(!navOpen);
  }

  function handleSearchChange(e) {
    setSearchQuery(e.target.value.toUpperCase());
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    if (searchQuery && !symbols.includes(searchQuery)) {
      setSymbols([searchQuery, ...symbols]);
      setSearchQuery("");
    }
  }

  function handleStockSelect(stock) {
    setSelectedStock(stock);
  }

  function openChat(stock) {
    setChatStock(stock);
    setChatOpen(true);
    setChatMessages([
      { role: "ai", text: `Welcome! Ask anything about ${stock}, or click 'Generate Report' for an instant AI analysis.` }
    ]);
    setChatInput("");
  }

  function closeChat() {
    setChatOpen(false);
    setChatStock(null);
    setChatMessages([]);
    setChatInput("");
  }

  function handleChatInput(e) {
    setChatInput(e.target.value);
  }

  function sendChatMessage(e) {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const userMsg = { role: "user", text: chatInput };
    setChatMessages(msgs => [...msgs, userMsg]);
    setChatInput("");
    // Mock AI response
    setTimeout(() => {
      let aiText = "This is a mock AI response. In the future, you'll get real-time insights here.";
      if (MOCK_AI_RESPONSES[chatStock]) {
        const idx = Math.floor(Math.random() * MOCK_AI_RESPONSES[chatStock].length);
        aiText = MOCK_AI_RESPONSES[chatStock][idx];
      }
      setChatMessages(msgs => [...msgs, { role: "ai", text: aiText }]);
    }, 900);
  }

  function generateReport() {
    // Simulate a full AI report
    if (MOCK_AI_RESPONSES[chatStock]) {
      setChatMessages(msgs => [
        ...msgs,
        { role: "user", text: "Generate Report" },
        ...MOCK_AI_RESPONSES[chatStock].map(text => ({ role: "ai", text }))
      ]);
    } else {
      setChatMessages(msgs => [
        ...msgs,
        { role: "user", text: "Generate Report" },
        { role: "ai", text: `No detailed report available for ${chatStock}.` }
      ]);
    }
  }

  return (
    <div className="dashboard-root">
      <div className="dashboard-main">
        <header className="dashboard-topbar">
          <div className="topbar-left">
            <div className="brand-name">
              Stock<span className="brand-ai">Ai</span>
            </div>
            <nav className={`topbar-nav${navOpen ? ' open' : ''}`}>
              <a href="#" className="topbar-link">
                <FaBookmark /> Bookmarks
              </a>
              <a href="#" className="topbar-link">
                <FaChartBar /> Trendings
              </a>
              <a href="#" className="topbar-link">
                <FaChartLine /> Analysis
              </a>
            </nav>
            <button className="nav-hamburger" onClick={handleNavToggle}>
              {navOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
          
          <div className="topbar-center">
            <div className="search-container">
              <FaSearch className="search-icon" />
              <input 
                className="topbar-search" 
                placeholder="Search stocks, crypto, or assets..." 
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={e => { if (e.key === 'Enter') handleSearchSubmit(e); }}
              />
            </div>
          </div>
          
          <div className="topbar-right">
            <button className="notification-btn" title="Notifications">
              <FaBell />
              <span className="notification-badge">3</span>
            </button>
            <button className="profile-btn" title="Profile">
              <FaUserCircle />
            </button>
          </div>
        </header>

        <div className="dashboard-content">
          <div className="content-header">
            <h1>Dashboard</h1>
            <p>Welcome back! Here's an overview of your portfolio and market insights.</p>
          </div>

          {/* Stock Market Section */}
          <section className="stock-section">
            <div className="stock-section-header">
              <h2>Indian Stock Market</h2>
              <form className="stock-search-form" onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  className="stock-search-input"
                  placeholder="Add symbol (e.g. WIPRO)"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  maxLength={10}
                  autoComplete="off"
                />
                <button type="submit" className="stock-search-btn"><FaSearch /></button>
              </form>
            </div>
            <div className="stock-table-wrapper">
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Price (₹)</th>
                    <th>Change</th>
                    <th>% Change</th>
                  </tr>
                </thead>
                <tbody>
                  {symbols.map(symbol => {
                    const d = MOCK_STOCKS[symbol] || { price: 1000.00, change: "+0.00", percentChange: "+0.00%" };
                    const isUp = d.change && d.change.startsWith("+");
                    const isSelected = selectedStock === symbol;
                    return (
                      <tr 
                        key={symbol} 
                        className={`${isUp ? "stock-up" : "stock-down"} ${isSelected ? "stock-selected" : ""}`}
                        onClick={() => handleStockSelect(symbol)}
                      >
                        <td>{symbol}</td>
                        <td>₹{d.price}</td>
                        <td>{d.change}</td>
                        <td>{d.percentChange}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Generate Report Button */}
            {selectedStock && (
              <div className="generate-report-section">
                <div className="selected-stock-info">
                  <span>Selected: <strong>{selectedStock}</strong></span>
                  <span className="selected-stock-price">
                    ₹{MOCK_STOCKS[selectedStock]?.price || "N/A"}
                  </span>
                </div>
                <button 
                  className="generate-report-btn-main" 
                  onClick={() => openChat(selectedStock)}
                >
                  Generate AI Report for {selectedStock}
                </button>
              </div>
            )}
          </section>

          {/* AI Chat Modal */}
          {chatOpen && (
            <div className="ai-chat-modal">
              <div className="ai-chat-box">
                <div className="ai-chat-header">
                  <span>AI Report for <b>{chatStock}</b></span>
                  <button className="ai-chat-close" onClick={closeChat} title="Close"><FaTimesCircle /></button>
                </div>
                <div className="ai-chat-messages">
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`ai-chat-message ai-chat-${msg.role}`}>{msg.text}</div>
                  ))}
                </div>
                <form className="ai-chat-input-row" onSubmit={sendChatMessage}>
                  <input
                    className="ai-chat-input"
                    placeholder="Type your question..."
                    value={chatInput}
                    onChange={handleChatInput}
                    autoFocus
                  />
                  <button type="submit" className="ai-chat-send-btn" title="Send"><FaRegPaperPlane /></button>
                  <button type="button" className="ai-chat-generate-btn" onClick={generateReport}>Generate Report</button>
                </form>
              </div>
              <div className="ai-chat-modal-bg" onClick={closeChat}></div>
            </div>
          )}

          <div className="dashboard-grid">
            <div className="card">
              <h3>Portfolio Overview</h3>
              <p>Track your investments and performance</p>
            </div>
            <div className="card">
              <h3>Market Trends</h3>
              <p>Latest market movements and insights</p>
            </div>
            <div className="card">
              <h3>Watchlist</h3>
              <p>Your saved stocks and assets</p>
            </div>
            <div className="card">
              <h3>Analytics</h3>
              <p>Detailed analysis and reports</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="dashboard-footer">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-brand">
                <div className="brand-name">
                  Stock<span className="brand-ai">Ai</span>
                </div>
                <p className="footer-tagline">Empowering your investment decisions with AI-driven insights and real-time market data.</p>
              </div>
            </div>
            
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul className="footer-links">
                <li><a href="#">Dashboard</a></li>
                <li><a href="#">Analytics</a></li>
                <li><a href="#">Portfolio</a></li>
                <li><a href="#">Watchlist</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Resources</h4>
              <ul className="footer-links">
                <li><a href="#">Market News</a></li>
                <li><a href="#">Research Reports</a></li>
                <li><a href="#">Trading Tools</a></li>
                <li><a href="#">Help Center</a></li>
              </ul>
            </div>
            
            <div className="footer-section">
              <h4>Connect</h4>
              <div className="social-links">
                <a href="#" className="social-link" title="Twitter">
                  <FaTwitter />
                </a>
                <a href="#" className="social-link" title="LinkedIn">
                  <FaLinkedin />
                </a>
                <a href="#" className="social-link" title="GitHub">
                  <FaGithub />
                </a>
                <a href="#" className="social-link" title="Email">
                  <FaEnvelope />
                </a>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p>&copy; 2025 StockAi. All rights reserved.</p>
              <div className="footer-bottom-links">
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
                <a href="#">Cookie Policy</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default Dashboard;